import { useEffect, useRef, useState } from "react";
import { FaStar, FaVideo, FaPenFancy, FaUserMd } from "react-icons/fa";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import TestimonialVideoModal, {
  type TestimonialVideoPayload,
} from "../../components/TestimonialVideoModal/TestimonialVideoModal";
import "./TestimonialsPage.scss";
import mentoringVideoThumb from "../../assets/testimonials/mentoring-video-thumb.png";

function StarRating({
  count = 5,
  size = "md",
  label,
}: {
  count?: number;
  size?: "sm" | "md" | "lg";
  label?: string;
}) {
  return (
    <div
      className={`tml-stars tml-stars--${size}`}
      role="img"
      aria-label={label ?? `${count} out of 5 stars`}
    >
      {Array.from({ length: count }, (_, i) => (
        <FaStar key={i} className="tml-stars__icon" aria-hidden />
      ))}
    </div>
  );
}

/** Placeholder clip for slots without a real video yet. */
const SAMPLE_VIDEO_SRC =
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.webm";

/** https://drive.google.com/file/d/1LKJNx6EZEiftAOLGLZfJSnDNPi52qnIb/view */
const VIDEO_DRIVE_ID_PRIMARY = "1LKJNx6EZEiftAOLGLZfJSnDNPi52qnIb";

/** https://drive.google.com/file/d/1R_AaL926VJgdZuPFF_miYdJMGGG5SOrz/view */
const MENTORING_VIDEO_DRIVE_ID = "1R_AaL926VJgdZuPFF_miYdJMGGG5SOrz";

type VideoTestimonial = {
  id: string;
  name: string;
  role: string;
  rating?: number;
  /** Custom poster image (imported asset path or URL). Overrides Drive auto-thumbnail. */
  thumbnail?: string;
} & (
  | { googleDriveFileId: string; src?: never; useSampleVideo?: never }
  | { src: string; googleDriveFileId?: never; useSampleVideo?: never }
  | { useSampleVideo: true; googleDriveFileId?: never; src?: never }
);

const VIDEO_TESTIMONIALS: VideoTestimonial[] = [
  {
    id: "v1",
    name: "Muneeb",
    role: "Coached by Diya",
    googleDriveFileId: VIDEO_DRIVE_ID_PRIMARY,
    thumbnail: mentoringVideoThumb,
  },
  {
    id: "v2",
    name: "Sara",
    role: "Coached by Diya",
    googleDriveFileId: MENTORING_VIDEO_DRIVE_ID,
  },
];

function toVideoPayload(item: VideoTestimonial): TestimonialVideoPayload {
  if ("googleDriveFileId" in item) {
    return {
      name: item.name,
      role: item.role,
      googleDriveFileId: item.googleDriveFileId,
    };
  }
  if (item.useSampleVideo) {
    return { name: item.name, role: item.role, useSampleVideo: true };
  }
  return { name: item.name, role: item.role, src: item.src };
}

function googleDriveThumbnailUrl(fileId: string) {
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w640`;
}

function inlineVideoSrc(item: VideoTestimonial): string | undefined {
  if ("googleDriveFileId" in item) return undefined;
  if (item.useSampleVideo) return SAMPLE_VIDEO_SRC;
  return item.src;
}

function VideoThumbnail({
  item,
  onPlay,
}: {
  item: VideoTestimonial;
  onPlay: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const driveId = "googleDriveFileId" in item ? item.googleDriveFileId : undefined;
  const videoSrc = inlineVideoSrc(item);
  const customThumb = item.thumbnail;
  const driveThumbSrc =
    !customThumb && driveId ? googleDriveThumbnailUrl(driveId) : undefined;
  const posterSrc = customThumb ?? driveThumbSrc;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoSrc || posterSrc) return;

    const showFirstFrame = () => {
      try {
        if (el.readyState >= 1) {
          el.currentTime = 0.1;
        }
      } catch {
        // ignore seek errors on short clips
      }
    };

    el.addEventListener("loadeddata", showFirstFrame);
    if (el.readyState >= 1) showFirstFrame();

    return () => el.removeEventListener("loadeddata", showFirstFrame);
  }, [videoSrc, posterSrc]);

  const rating = item.rating ?? 5;

  return (
    <figure className="tml-video">
      <button
        type="button"
        className="tml-video__trigger"
        onClick={onPlay}
        aria-label={`Play video: ${item.name}`}
      >
        <div className="tml-video__frame">
          <span className="tml-video__badge" aria-hidden>
            <FaStar className="tml-video__badge-icon" />
            {rating}.0
          </span>
          {posterSrc ? (
            <img
              src={posterSrc}
              alt=""
              className="tml-video__thumb-media"
            />
          ) : videoSrc ? (
            <video
              ref={videoRef}
              src={videoSrc}
              preload="auto"
              muted
              playsInline
              tabIndex={-1}
              aria-hidden
              className="tml-video__thumb-media"
            />
          ) : (
            <span className="tml-video__poster-fallback" aria-hidden />
          )}
          <span className="tml-video__play" aria-hidden>
            <span className="tml-video__play-triangle" />
          </span>
        </div>
      </button>
      <figcaption className="tml-video__caption">
        <span className="tml-video__name">{item.name}</span>
        <span className="tml-video__role">{item.role}</span>
      </figcaption>
    </figure>
  );
}

type TextTestimonial = {
  id: string;
  name: string;
  meta: string;
  rating?: number;
} & (
  | { quote: string; paragraphs?: never }
  | { paragraphs: string[]; quote?: never }
);

const TEXT_TESTIMONIALS: TextTestimonial[] = [
  {
    id: "t0",
    paragraphs: [
      "Getting into ST1 ophthalmology felt like an overwhelming prospect, but Diya made the process feel genuinely manageable. From the outset, he was incredibly generous with his time, always approachable and willing to answer my questions, no matter how small. He helped me with my application in a way that felt truly tailored to me, taking the time to understand my personal circumstances and preferences before offering thoughtful, specific advice on which deaneries to consider. That level of care and personalisation made a real difference.",
      "I have no doubt that Diya's support was a significant factor in my success, and I feel very lucky to have had him in my corner. If you're navigating the ophthalmology application process, I can't recommend his mentoring highly enough, he brings both the expertise and the genuine warmth that every applicant needs.",
    ],
    name: "Isaam",
    meta: "Ophthalmology application mentoring (coached by Diya)",
    rating: 5,
  },
];

function WrittenItem({ item }: { item: TextTestimonial }) {
  const featured = Boolean(item.paragraphs);
  const rating = item.rating ?? 5;

  return (
    <blockquote className={`tml-written${featured ? " tml-written--featured" : ""}`}>
      <div className="tml-written__rating">
        <StarRating count={rating} size={featured ? "md" : "sm"} />
        <span className="tml-written__rating-label">{rating}.0</span>
      </div>
      {item.paragraphs ? (
        item.paragraphs.map((para, idx) => (
          <p key={idx} className="tml-written__text">
            {para}
          </p>
        ))
      ) : (
        <p className="tml-written__text">{item.quote}</p>
      )}
      <footer className="tml-written__footer">
        <cite className="tml-written__name">{item.name}</cite>
        <span className="tml-written__meta">{item.meta}</span>
      </footer>
    </blockquote>
  );
}

const TestimonialsPage = () => {
  const [modalVideo, setModalVideo] = useState<TestimonialVideoPayload | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const featuredWritten = TEXT_TESTIMONIALS.filter((t) => t.paragraphs);
  const shortWritten = TEXT_TESTIMONIALS.filter((t) => !t.paragraphs);
  const testimonialCount = VIDEO_TESTIMONIALS.length + TEXT_TESTIMONIALS.length;

  return (
    <div id="TestimonialsPage">
      <NavBar />

      <TestimonialVideoModal
        show={modalVideo !== null}
        onHide={() => setModalVideo(null)}
        video={modalVideo}
      />

      <header className="tml-hero">
        <div className="site-container">
          <p className="tml-eyebrow">Trainee feedback</p>
          <h1 className="tml-hero__title">
            What doctors <span className="tml-hero__title-accent">say</span>
          </h1>
          <p className="tml-hero__lead">
            Video and written feedback from doctors preparing for FRCOphth, the MSRA, and ophthalmology
            interviews — many coached directly by <strong>Diya</strong>.
          </p>

          {/* <div className="tml-hero__rating-block">
            <StarRating size="lg" />
            <div className="tml-hero__rating-copy">
              <span className="tml-hero__score">5.0</span>
              <span className="tml-hero__rating-text">average from trainee feedback</span>
            </div>
          </div> */}

          {/* <ul className="tml-hero__stats" aria-label="Testimonial highlights">
            <li className="tml-stat tml-stat--gold">
              <FaStar className="tml-stat__icon" aria-hidden />
              <span className="tml-stat__value">5.0</span>
              <span className="tml-stat__label">star rating</span>
            </li>
            <li className="tml-stat tml-stat--blue">
              <FaVideo className="tml-stat__icon" aria-hidden />
              <span className="tml-stat__value">{VIDEO_TESTIMONIALS.length}</span>
              <span className="tml-stat__label">video clips</span>
            </li>
            <li className="tml-stat tml-stat--navy">
              <FaPenFancy className="tml-stat__icon" aria-hidden />
              <span className="tml-stat__value">{TEXT_TESTIMONIALS.length}</span>
              <span className="tml-stat__label">written reviews</span>
            </li>
            <li className="tml-stat tml-stat--teal">
              <FaUserMd className="tml-stat__icon" aria-hidden />
              <span className="tml-stat__value">{testimonialCount}</span>
              <span className="tml-stat__label">shared stories</span>
            </li>
          </ul> */}
        </div>
      </header>

      <main className="tml-main">
        <div className="site-container">
          <section className="tml-section" aria-labelledby="tml-videos-heading">
            <header className="tml-section__header">
              <p className="tml-section__label">Video</p>
              <h2 id="tml-videos-heading" className="tml-section__title">
                Hear it in their own words
              </h2>
              <p className="tml-section__intro">
                Short clips from trainees who have worked with Diya on exam prep and applications.
              </p>
            </header>
            <div className="tml-video-list">
              {VIDEO_TESTIMONIALS.map((item) => (
                <VideoThumbnail
                  key={item.id}
                  item={item}
                  onPlay={() => setModalVideo(toVideoPayload(item))}
                />
              ))}
            </div>
          </section>

          <section
            className="tml-section tml-section--alt"
            aria-labelledby="tml-written-heading"
          >
            <header className="tml-section__header">
              <p className="tml-section__label tml-section__label--written">Written</p>
              <h2 id="tml-written-heading" className="tml-section__title">
                In more detail
              </h2>
              <p className="tml-section__intro">
                Longer reflections on what mentoring and preparation looked like in practice.
              </p>
            </header>

            <div className="tml-written-stack">
              {featuredWritten.map((item) => (
                <WrittenItem key={item.id} item={item} />
              ))}
            </div>

            {shortWritten.length > 0 && (
              <div className="tml-written-grid">
                {shortWritten.map((item) => (
                  <WrittenItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
